/* eslint-disable no-use-before-define */
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
import { v4 as newGuid } from 'uuid';
import { AsantoAffordabilityResiRequest, AsantoApplicantResi, MortgageType, PropertyType } from '../../models';
import { IPensionIncomeInputs, IUiAffordabilityModel, IUiApplicant } from '../../models/affordability-ui';
import { employmentStatusMap, getAge, twoDigits } from './shared';

export function uiModelToAsantoResiRequest(model: IUiAffordabilityModel) {
  return {
    search_id: newGuid(),
    num_applicants: model.applicants.numberOfApplicants,
    property_value: model.propertyAndLoan.propertyValue,
    loan_amount: model.propertyAndLoan.loanAmount,
    mortgage_type: MortgageType[model.propertyAndLoan.mortgageType],
    property_type: PropertyType[model.propertyAndLoan.propertyType],
    joint_total_credit_balance: model.applicants.jointCommitments?.creditCardBalance ?? 0,
    joint_annual_loan_payments: (model.applicants.jointCommitments?.regularMonthlyCommitments ?? 0) * 12,
    applicants: model.incomeAndExpenditure.applicants
      .map((app, i) => applicantUiModelToAsantoRequestResi(app, i, model)),
  } as AsantoAffordabilityResiRequest;
}

export function applicantUiModelToAsantoRequestResi(applicant: IUiApplicant, index: number,
  model: IUiAffordabilityModel) {
  const { dateOfBirth } = model.incomeAndExpenditure.applicants[index];

  const [remainingAnnualNonEscalatingAnnuityValue, remainingAnnualNonEscalatingAnnuityPercentage] =
    calculateRemainingIncome(applicant.annualNonEscalatingAnnuity);

  const [remainingDefinedContributionValue, remainingDefinedContributionPercentage] =
    calculateRemainingIncome(applicant.definedContribution);

  const [remainingAnnualEscalatingAnnuityValue, remainingAnnualEscalatingAnnuityPercentage] =
    calculateRemainingIncome(applicant.annualEscalatingAnnuity);

  const [remainingDefinedBenefitFinalSalaryPensionValue, remainingDefinedBenefitFinalSalaryPensionPercentage] =
    calculateRemainingIncome(applicant.definedBenefitFinalSalaryPension);

  const [remainingForecastDefinedBenefitFinalSalaryPensionValue,
    remainingForecastDefinedBenefitFinalSalaryPensionPercentage] =
    calculateRemainingIncome(applicant.forecastDefinedBenefitFinalSalaryPension);

  const [remainingCurrentDrawdownPensionFundValueValue, remainingCurrentDrawdownPensionFundValuePercentage] =
    calculateRemainingIncome(applicant.currentDrawdownPensionFundValue);

  return {
    applicant_number: index + 1,

    // Personal details
    date_of_birth: `${dateOfBirth.year}-${twoDigits(dateOfBirth.month)}-${twoDigits(dateOfBirth.day)}`,
    current_age: getAge(new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day)),
    planned_retirement_age: applicant.primaryIncome?.plannedRetirementAge ?? 0,
    employment_status: applicant.primaryIncome?.employmentStatus
      ? employmentStatusMap.get(applicant.primaryIncome.employmentStatus) : 'N/A',

    dependants_0_to_5: index === 0 && model.applicants.dependantsAged0To5 ? model.applicants.dependantsAged0To5 : 0,
    dependants_6_to_11: index === 0 && model.applicants.dependantsAged6To11 ? model.applicants.dependantsAged6To11 : 0,
    dependants_12_to_17: index === 0 && model.applicants.dependantsAged12To17 ?
      model.applicants.dependantsAged12To17 : 0,
    dependants_18_and_over: index === 0 && model.applicants.dependantsAged18AndOver ?
      model.applicants.dependantsAged18AndOver : 0,

    // Primary income
    annual_gross_salary: applicant.primaryIncome?.grossAnnualIncome ?? 0,
    annual_bonus: applicant.primaryIncome?.annualBonusCommissionOvertime ?? 0,
    annual_net_profit_current: applicant.primaryIncome?.netProfitForLatestPeriod ?? 0,
    annual_net_profit_previous: applicant.primaryIncome?.netProfitForPreviousPeriod ?? 0,
    annual_directors_salary_current: applicant.primaryIncome?.grossRemunerationForLatestPeriod ?? 0,
    annual_directors_salary_previous:  applicant.primaryIncome?.grossRemunerationForPreviousPeriod ?? 0,
    // Other Income
    annual_buy_to_let: applicant.btlInvestmentIncome ?? 0,
    annual_buy_to_let_percentage: applicant.btlInvestmentIncomeBenefit,
    annual_investment_income_guaranteed: applicant.growthInvestmentAnnualIncome ?? 0,
    annual_investment_income_guaranteed_percentage: applicant.growthInvestmentAnnualIncomeDependents ?? 0,
    annual_investment_income_non_guaranteed: applicant.nonGuaranteedInvestmentIncome ?? 0,
    annual_investment_income_non_guaranteed_percentage: applicant.nonGuaranteedInvestmentIncomeDependent,
    annual_lir_disability_benefits: applicant.disabilityBenefit ?? 0,
    second_job_annual_gross_salary: applicant.annualAdditionalIncome ?? 0,

    // Current retirement income
    annual_state_pension: applicant.annualStatePension ?? 0,
    // annual_flexi_pension_record
    annual_flexi_pension_record_1: applicant.definedContribution[0]?.value ?? 0,
    annual_flexi_pension_record_percentage_1: applicant.definedContribution[0]?.percentage ?? 0,
    annual_flexi_pension_record_2: applicant.definedContribution[1]?.value ?? 0,
    annual_flexi_pension_record_percentage_2: applicant.definedContribution[1]?.percentage ?? 0,
    annual_flexi_pension_record_3: applicant.definedContribution[2]?.value ?? 0,
    annual_flexi_pension_record_percentage_3: applicant.definedContribution[2]?.percentage ?? 0,
    annual_flexi_pension_record_4: remainingDefinedContributionValue ?? 0,
    annual_flexi_pension_record_percentage_4: remainingDefinedContributionPercentage ?? 0,
    // annual_non_escalating_annuity
    annual_non_escalating_annuity_1: applicant.annualNonEscalatingAnnuity[0]?.value ?? 0,
    annual_non_escalating_annuity_percentage_1:applicant.annualNonEscalatingAnnuity[0]?.percentage ?? 0,
    annual_non_escalating_annuity_2: applicant.annualNonEscalatingAnnuity[1]?.value ?? 0,
    annual_non_escalating_annuity_percentage_2: applicant.annualNonEscalatingAnnuity[1]?.percentage ?? 0,
    annual_non_escalating_annuity_3: applicant.annualNonEscalatingAnnuity[2]?.value ?? 0,
    annual_non_escalating_annuity_percentage_3: applicant.annualNonEscalatingAnnuity[2]?.percentage ?? 0,
    annual_non_escalating_annuity_4: remainingAnnualNonEscalatingAnnuityValue ?? 0,
    annual_non_escalating_annuity_percentage_4: remainingAnnualNonEscalatingAnnuityPercentage ?? 0,
    // annual_escalating_annuity
    annual_escalating_annuity_1:  applicant.annualEscalatingAnnuity[0]?.value ?? 0,
    annual_escalating_annuity_percentage_1: applicant.annualEscalatingAnnuity[0]?.percentage ?? 0,
    annual_escalating_annuity_2:  applicant.annualEscalatingAnnuity[1]?.value ?? 0,
    annual_escalating_annuity_percentage_2:  applicant.annualEscalatingAnnuity[1]?.percentage ?? 0,
    annual_escalating_annuity_3:  applicant.annualEscalatingAnnuity[2]?.value ?? 0,
    annual_escalating_annuity_percentage_3:  applicant.annualEscalatingAnnuity[2]?.percentage ?? 0,
    annual_escalating_annuity_4:  remainingAnnualEscalatingAnnuityValue ?? 0,
    annual_escalating_annuity_percentage_4:  remainingAnnualEscalatingAnnuityPercentage ?? 0,

    //  annual_flexi_pension
    annual_occupation_pension_record_1: applicant.definedBenefitFinalSalaryPension[0]?.value ?? 0,
    annual_occupation_pension_record_percentage_1:  applicant.definedBenefitFinalSalaryPension[0]?.percentage ?? 0,
    annual_occupation_pension_record_2: applicant.definedBenefitFinalSalaryPension[1]?.value ?? 0,
    annual_occupation_pension_record_percentage_2:  applicant.definedBenefitFinalSalaryPension[1]?.percentage ?? 0,
    annual_occupation_pension_record_3:  applicant.definedBenefitFinalSalaryPension[2]?.value ?? 0,
    annual_occupation_pension_record_percentage_3: applicant.definedBenefitFinalSalaryPension[2]?.percentage ?? 0,
    annual_occupation_pension_record_4:  remainingDefinedBenefitFinalSalaryPensionValue ?? 0,
    annual_occupation_pension_record_percentage_4:  remainingDefinedBenefitFinalSalaryPensionPercentage ?? 0,

    // Future retirement income
    annual_lir_state_pension: applicant.expectedAnnualStatePension ?? 0,
    // annual_lir_pension_benefit_record_1
    annual_lir_pension_benefit_record_1: applicant.forecastDefinedBenefitFinalSalaryPension[0]?.value ?? 0,
    annual_lir_pension_benefit_record_percentage_1:
      applicant.forecastDefinedBenefitFinalSalaryPension[0]?.percentage ?? 0,
    annual_lir_pension_benefit_record_2: applicant.forecastDefinedBenefitFinalSalaryPension[1]?.value ?? 0,
    annual_lir_pension_benefit_record_percentage_2:
      applicant.forecastDefinedBenefitFinalSalaryPension[1]?.percentage ?? 0,
    annual_lir_pension_benefit_record_3: applicant.forecastDefinedBenefitFinalSalaryPension[2]?.value ?? 0,
    annual_lir_pension_benefit_record_percentage_3:
      applicant.forecastDefinedBenefitFinalSalaryPension[2]?.percentage ?? 0,
    annual_lir_pension_benefit_record_4: remainingForecastDefinedBenefitFinalSalaryPensionValue ?? 0,
    annual_lir_pension_benefit_record_percentage_4: remainingForecastDefinedBenefitFinalSalaryPensionPercentage ?? 0,

    // annual_lir_pension_contribution_record
    annual_lir_pension_contribution_record_1: applicant.currentDrawdownPensionFundValue[0]?.value ?? 0,
    annual_lir_pension_contribution_record_percentage_1: applicant.currentDrawdownPensionFundValue[0]?.percentage ?? 0,
    annual_lir_pension_contribution_record_2: applicant.currentDrawdownPensionFundValue[1]?.value ?? 0,
    annual_lir_pension_contribution_record_percentage_2: applicant.currentDrawdownPensionFundValue[1]?.percentage ?? 0,
    annual_lir_pension_contribution_record_3: applicant.currentDrawdownPensionFundValue[2]?.value ?? 0,
    annual_lir_pension_contribution_record_percentage_3: applicant.currentDrawdownPensionFundValue[2]?.percentage ?? 0,
    annual_lir_pension_contribution_record_4: remainingCurrentDrawdownPensionFundValueValue ?? 0,
    annual_lir_pension_contribution_record_percentage_4: remainingCurrentDrawdownPensionFundValuePercentage ?? 0,

    // Individual Commitments - also map joint fields to first applicant
    total_credit_card_balance: applicant.creditCardBalance ?? 0,
    annual_loan_payments: (applicant.regularMonthlyCommitments ?? 0) * 12,
    annual_council_tax: model.applicants.numberOfApplicants > 1 && index === 0
      ? Number(applicant.councilTax ?? 0) + Number(model.applicants.jointCommitments?.councilTax)
      : applicant.councilTax ?? 0,
  } as AsantoApplicantResi;
}

function calculateRemainingIncome(income: IPensionIncomeInputs[]): [value: number, percentage
  : number] {
  const remainingElements = income.slice(3);
  const totalValue = remainingElements.reduce((accumulator, income) => {
    return accumulator + +income.value;
  }, 0);
  const totalPercentage = remainingElements.reduce((accumulator, income) => {
    return accumulator + (+income.value / totalValue) * +income.percentage;
  }, 0);
  return [totalValue, totalPercentage];
}
